import React, { useEffect, useMemo, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Modal,
  TextField,
  Button,
  Chip,
  CircularProgress,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import {
  DEFAULT_INVITE_MEMBER_VALUES,
  buildInviteMemberSchema,
} from './validationSchema';
import { InviteMemberFormData, InviteMemberModalProps } from './types';
import {
  FooterActions,
  FormFields,
  InviteAutocompletePopper,
  StyledPaper,
  StyledTitle,
} from './styles';
import { useInviteTeamOptions } from './useInviteTeamOptions';
import { useInviteMemberOptions } from './useInviteMemberOptions';
import { useCreateInvitation } from './useCreateInvitation';

function mergeInitialValues(
  initialValues?: Partial<InviteMemberFormData> | null,
): InviteMemberFormData {
  return {
    ...DEFAULT_INVITE_MEMBER_VALUES,
    ...initialValues,
    roles: initialValues?.roles ?? DEFAULT_INVITE_MEMBER_VALUES.roles,
    applications:
      initialValues?.applications ?? DEFAULT_INVITE_MEMBER_VALUES.applications,
  };
}

export const InviteMemberModal = ({
  isOpen,
  onClose,
  initialValues,
  invitations = [],
  ignoreInvitationId,
  onInvitationSent,
}: InviteMemberModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const createInvitationMutation = useCreateInvitation();
  const { teamOptions, isLoading: isTeamsLoading } =
    useInviteTeamOptions(isOpen);

  const schema = useMemo(
    () => buildInviteMemberSchema(invitations, ignoreInvitationId),
    [ignoreInvitationId, invitations],
  );

  const { control, handleSubmit, reset, watch, setValue, getValues } =
    useForm<InviteMemberFormData>({
      resolver: yupResolver(schema),
      defaultValues: DEFAULT_INVITE_MEMBER_VALUES,
      mode: 'onSubmit',
      reValidateMode: 'onSubmit',
    });

  const selectedTeamId = watch('teamId');

  const {
    applicationOptions,
    roleOptions,
    isLoadingApplications,
    isLoadingRoles,
  } = useInviteMemberOptions(selectedTeamId, isOpen);

  const previousTeamIdRef = useRef<string | undefined>();

  useEffect(() => {
    const previousTeamId = previousTeamIdRef.current;
    previousTeamIdRef.current = selectedTeamId;

    if (
      !selectedTeamId ||
      previousTeamId === undefined ||
      previousTeamId === selectedTeamId
    ) {
      return;
    }

    setValue('applications', []);
    setValue('roles', []);
  }, [selectedTeamId, setValue]);

  useEffect(() => {
    const currentRoles = getValues('roles');
    const allowedRoleIds = new Set(roleOptions.map(role => role.id));
    const nextRoles = currentRoles.filter(roleId => allowedRoleIds.has(roleId));
    if (nextRoles.length !== currentRoles.length) {
      setValue('roles', nextRoles);
    }
  }, [getValues, roleOptions, setValue]);

  const resetTo = (values: InviteMemberFormData) => {
    reset(values);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetTo(mergeInitialValues(initialValues));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialValues]);

  const handleClose = () => {
    previousTeamIdRef.current = undefined;
    resetTo(DEFAULT_INVITE_MEMBER_VALUES);
    onClose();
  };

  const onSubmit = async (data: InviteMemberFormData) => {
    try {
      const result = await createInvitationMutation.mutateAsync(data);
      onInvitationSent?.(result);
      enqueueSnackbar(
        <span>
          An invitation to user <strong>{data.email}</strong> has been sent
        </span>,
        { variant: 'success' },
      );
      handleClose();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to send invitation'), {
        variant: 'error',
      });
    }
  };

  const isSubmitting = createInvitationMutation.isPending;

  return (
    <Modal
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1301,
      }}
      open={isOpen}
      onClose={handleClose}
      disableEnforceFocus
    >
      <StyledPaper elevation={0}>
        <StyledTitle variant="h6">Create Invitation</StyledTitle>

        <FormFields>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                variant="outlined"
                label="Email"
                type="email"
                required
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name="teamId"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const selectedTeam =
                teamOptions.find(option => option.id === field.value) ?? null;

              return (
                <Autocomplete
                  options={teamOptions}
                  value={selectedTeam}
                  disabled={isTeamsLoading}
                  loading={isTeamsLoading}
                  getOptionLabel={option => option.name}
                  getOptionSelected={(option, selected) =>
                    option.id === selected.id
                  }
                  onChange={(_, option) => field.onChange(option?.id ?? '')}
                  PopperComponent={InviteAutocompletePopper}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Team"
                      required
                      placeholder="Select team"
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isTeamsLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              );
            }}
          />
          <Controller
            name="applications"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                multiple
                options={applicationOptions}
                value={applicationOptions.filter(option =>
                  field.value.includes(option.value),
                )}
                disabled={!selectedTeamId || isLoadingApplications}
                loading={isLoadingApplications}
                getOptionLabel={option => option.label}
                getOptionSelected={(option, selected) =>
                  option.value === selected.value
                }
                onChange={(_, options) =>
                  field.onChange(options.map(option => option.value))
                }
                filterSelectedOptions
                PopperComponent={InviteAutocompletePopper}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.label}
                      color="primary"
                    />
                  ))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Applications"
                    required
                    placeholder={
                      field.value.length ? '' : 'Select applications'
                    }
                    InputLabelProps={{ shrink: true }}
                    error={!!error}
                    helperText={
                      error?.message ??
                      (!selectedTeamId ? 'Select a team first' : undefined)
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingApplications ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
          <Controller
            name="roles"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const selectedRoles = roleOptions.filter(role =>
                field.value.includes(role.id),
              );

              return (
                <Autocomplete
                  multiple
                  options={roleOptions}
                  value={selectedRoles}
                  disabled={!selectedTeamId || isLoadingRoles}
                  loading={isLoadingRoles}
                  getOptionLabel={option => option.label}
                  getOptionSelected={(option, selected) =>
                    option.id === selected.id
                  }
                  onChange={(_, options) =>
                    field.onChange(options.map(option => option.id))
                  }
                  filterSelectedOptions
                  PopperComponent={InviteAutocompletePopper}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.label}
                        color="primary"
                      />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Roles"
                      required
                      placeholder={field.value.length ? '' : 'Select roles'}
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                      helperText={
                        error?.message ??
                        (!selectedTeamId ? 'Select a team first' : undefined)
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingRoles ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              );
            }}
          />
        </FormFields>

        <FooterActions>
          <Button onClick={handleClose} color="primary" variant="text">
            Dismiss
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending…' : 'Send Invitation'}
          </Button>
        </FooterActions>
      </StyledPaper>
    </Modal>
  );
};
